import { useEffect, useState, useRef } from "react";
import api from "../../services/api";

import editIcon from "../../assets/edit-icon.png";
import deleteIcon from "../../assets/delete-icon.png";

const COLORS = {
  primary: "#0D47A1",
  danger: "#D32F2F",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
};

const Units = () => {
  const [units, setUnits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("primary");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formRef = useRef(null);

  const loadUnits = async () => {
    try {
      const res = await api.get("/units/list.php");
      setUnits(res.data?.data || []);
    } catch {
      setError("Failed to load units");
    }
  };

  useEffect(() => {
    loadUnits();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showForm && formRef.current && !formRef.current.contains(e.target)) {
        resetForm();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName("");
    setType("primary");
    setError("");
  };

  const validate = () => {
    if (!name.trim()) return "Unit name required";
    if (!type) return "Unit type required";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: name.trim().toUpperCase(), // Match PHP strtoupper
        unit_type: type.toLowerCase(),   // Match PHP strtolower
      };

      if (editingId) {
        await api.post("/units/update.php", {
          id: editingId,
          ...payload,
        });
      } else {
        await api.post("/units/create.php", payload);
      }

      resetForm();
      loadUnits();
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (unit) => {
    setName(unit.name);
    setType(unit.unit_type);
    setEditingId(unit.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this unit?")) return;

    try {
      await api.post("/units/delete.php", { id });
      loadUnits();
    } catch {
      setError("Delete failed");
    }
  };

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Units</h2>
          <p style={styles.subtitle}>Manage measurement units</p>
        </div>

        <button
          style={styles.addBtn}
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setName("");
            setType("primary");
            setError("");
          }}
        >
          + Add Unit
        </button>
      </div>

      {/* FORM */}
      {showForm && (
        <div ref={formRef} style={styles.formCard}>
          <form onSubmit={submit} style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.label}>Unit Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. PCS"
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Unit Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={styles.input}
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
              </select>
            </div>

            <div style={styles.buttonWrap}>
              <button type="submit" style={styles.saveBtn} disabled={loading}>
                {loading
                  ? editingId
                    ? "Updating..."
                    : "Adding..."
                  : editingId
                  ? "Update"
                  : "Save"}
              </button>
            </div>
          </form>

          {error && <p style={styles.error}>{error}</p>}
        </div>
      )}

      {/* TABLE */}
      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Type</th>
                <th style={styles.thCenter}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {units.length === 0 && (
                <tr>
                  <td colSpan="4" style={styles.empty}>
                    No units found
                  </td>
                </tr>
              )}

              {units.map((u, index) => (
                <tr key={u.id}>
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.tdBold}>{u.name}</td>
                  <td style={styles.td}>
                    <span style={{ textTransform: "capitalize" }}>
                        {u.unit_type}
                    </span>
                  </td>

                  <td style={styles.actionCell}>
                    <div style={styles.iconWrapper}>
                      <button
                        style={styles.iconBtn}
                        onClick={() => handleEdit(u)}
                      >
                        <img src={editIcon} alt="edit" style={styles.icon} />
                      </button>

                      <button
                        style={styles.iconBtn}
                        onClick={() => handleDelete(u.id)}
                      >
                        <img src={deleteIcon} alt="delete" style={styles.icon} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ================= STYLES ================= */

const styles = {
  page: {
    background: COLORS.bg,
    minHeight: "100vh",
    padding: "clamp(12px, 4vw, 30px)",
    boxSizing: "border-box",
    overflowX: "hidden",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 20,
  },

  title: {
    fontWeight: 700,
    fontSize: "clamp(18px, 2.5vw, 26px)",
    marginBottom: 4,
    color: COLORS.text,
  },

  subtitle: {
    fontSize: "clamp(12px, 2vw, 14px)",
    color: COLORS.muted,
  },

  addBtn: {
    background: COLORS.primary,
    color: "#fff",
    padding: "10px 16px",
    borderRadius: 10,
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
  },

  formCard: {
    background: COLORS.card,
    padding: "clamp(15px,3vw,25px)",
    borderRadius: 16,
    marginBottom: 20,
    border: `1px solid ${COLORS.border}`,
  },

  formRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", // Responsive grid
    gap: 12,
  },

  field: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  label: {
    fontSize: 13,
    marginBottom: 6,
    color: COLORS.muted,
  },

  input: {
    width: "100%",
    padding: "12px",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    boxSizing: "border-box",
    fontSize: 14,
  },

  buttonWrap: {
    display: "flex",
    alignItems: "flex-end",
    width: "100%",
  },

  saveBtn: {
    background: COLORS.primary,
    color: "#fff",
    border: "none",
    padding: "12px 16px",
    borderRadius: 8,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
    height: 44, // Match input height
  },

  card: {
    background: COLORS.card,
    borderRadius: 18,
    padding: "clamp(12px,2vw,25px)",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    textAlign: "left",
    padding: "12px",
    fontSize: 14,
    color: COLORS.muted,
    borderBottom: `1px solid ${COLORS.border}`,
    whiteSpace: "nowrap",
  },

  thCenter: {
    textAlign: "center",
    padding: "12px",
    fontSize: 14,
    color: COLORS.muted,
    borderBottom: `1px solid ${COLORS.border}`,
  },

  td: {
    padding: "14px 12px",
    borderBottom: `1px solid ${COLORS.border}`,
    fontSize: 14,
  },

  tdBold: {
    padding: "14px 12px",
    fontWeight: 600,
    borderBottom: `1px solid ${COLORS.border}`,
    fontSize: 14,
  },

  actionCell: {
    borderBottom: `1px solid ${COLORS.border}`,
    textAlign: "center",
  },

  iconWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
  },

  iconBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },

  icon: {
    width: 18,
    height: 18,
  },

  empty: {
    textAlign: "center",
    padding: 20,
    color: COLORS.muted,
  },

  error: {
    marginTop: 10,
    color: COLORS.danger,
    fontSize: 13,
  },
};

export default Units;